import PropTypes from "prop-types";
import React, { createContext, useContext, useEffect, useRef, useState } from "react";

import { fireTrack } from "@swa-ui/analytics";
import { ApplicationContext, useAppSetting } from "@swa-ui/application";
import { getBootstrapData } from "@swa-ui/bootstrap";
import { useDeviceInfo, useHistory, window } from "@swa-ui/browser";
import { AriaLive } from "@swa-ui/core";
import { isResponseOk, useFetch } from "@swa-ui/fetch";
import { decodeMessage, isHybridEnabled, useHybrid, WEBVIEW_MESSAGE_KEYS } from "@swa-ui/hybrid";
import i18n from "@swa-ui/locale";
import { logger } from "@swa-ui/log";
import { usePersistedState } from "@swa-ui/persistence";
import { usePersonas } from "@swa-ui/personas";
import { personas } from "@swa-ui/personas/defines";

import { decodeCookie, getCookie, setCookie } from "../cookie";
import { corporateRoles } from "../defines";
import {
  removeCookies,
  removeCorporateCookies,
  removeCorporateStores,
  removeMwebStores,
  removeVisionStores,
} from "../logoutHandler";
import { getStoredLoginInfoSchema } from "../schema/loginSchema";

const availableLocales = ["en", "es"];

export const AuthContext = createContext();
export const AuthProvider = (props) => {
  const { children, environmentTier, site } = props;
  const {
    error: tokenExchangeError,
    loading: loadingTokenExchange,
    post: postTokenExchange,
    response: responseTokenExchange,
  } = useFetch("", (globalOptions) => {
    globalOptions.headers["Content-Type"] = "application/x-www-form-urlencoded";

    return globalOptions;
  });
  const { post: postLogin, response: responseLogin } = useFetch("", (globalOptions) => {
    globalOptions.headers["Content-Type"] = "application/x-www-form-urlencoded";

    return globalOptions;
  });
  const { isCorporatePersona, resetPersona, setPersona } = usePersonas();
  const { post: postLogout } = useFetch();
  const { hybridEnabled, appId } = useContext(ApplicationContext);
  const { isSwaWebView } = useDeviceInfo();
  const [auth, setAuth] = usePersistedState({
    defaultValue: {},
    key: "AuthProvider-auth",
  });
  const [storedLoginInfo, setStoredLoginInfo] = usePersistedState({
    defaultValue: {
      rememberMe: false,
      username: "",
    },
    key: "storedLoginInfo",
    persistDefault: true,
    schema: getStoredLoginInfoSchema(),
    storageType: "local",
  });
  const [doTokenDowngradeOnLoad, setDoTokenDowngradeOnLoad] = usePersistedState({
    defaultValue: false,
    key: "AuthProvider-doTokenDowngradeOnLoad",
  });
  const [ariaLiveMessage, setAriaLiveMessage] = useState("");
  const [prepareTokenDowngradeOnUnload, setPrepareTokenDowngradeOnUnload] = useState(true);
  const logoutHandlersRef = useRef({});
  const nativeLoginCancelRef = useRef({});
  const nativeLoginHandlersRef = useRef({});
  const preLogoutHandlersRef = useRef({});
  const additionalMessagesFromNativeApp = {
    [WEBVIEW_MESSAGE_KEYS.AUTH_EVENT]: handleAuthEventWebViewMessage,
    [WEBVIEW_MESSAGE_KEYS.ADD_OAUTH]: handleAddOauthWebViewMessage,
    [WEBVIEW_MESSAGE_KEYS.REMOVE_OAUTH]: handleRemoveOauthWebViewMessage,
  };
  const history = useHistory();
  const isMobileSite = site === "mobile";
  const isCorporateSite = site === "corporate";
  const loginAvailable = useAppSetting("loginAvailable", false);
  const loginSettings = getBootstrapData("login-settings");
  const tokenExchangeInProgress = useRef(false);
  const missingInformation = auth["customers.userInformation.missingInformation"];
  const missingCorporateInformation = auth["corporate.corporateUserInformation.missingInformation"];

  hybridEnabled &&
    isHybridEnabled() &&
    useHybrid({ additionalMessagesFromNativeApp, appId, history });

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const companyIdParam = urlParams.get("companyId");
    const idToken = getCookie("id_token");
    const tokenCookie = getCookie("token");

    shouldHandleTokenExchange() && handleTokenExchangeAndMarkInProgress(tokenCookie);
    loginAvailable && !!auth.idToken && !getCookie("id_token") && cleanUpSession();
    doTokenDowngradeOnLoad && !!getCompanyIdFromToken() && !isCorporateSite && switchAuthType();
    shouldSetAuth() && setAuth({ idToken: idToken });
    setCookie("locale", getCurrentLocale());
    setDoTokenDowngradeOnLoad(false);
    isCorporatePersona && companyIdParam && !isAuthorized() && handleAnonymousLogin(companyIdParam);
  }, []);

  useEffect(() => {
    function handlePrepareTokenDowngrade() {
      if (prepareTokenDowngradeOnUnload && !isCorporateSite && getCompanyIdFromToken()) {
        setDoTokenDowngradeOnLoad(true);
      }
    }
    window.addEventListener("beforeunload", handlePrepareTokenDowngrade);

    return () => {
      window.removeEventListener("beforeunload", handlePrepareTokenDowngrade);
    };
  }, [prepareTokenDowngradeOnUnload]);

  useEffect(() => {
    if (auth.corporateToken) {
      setPersona(personas.corporate);
    } else {
      resetPersona();
    }
  }, [auth]);

  useEffect(() => {
    if (loadingTokenExchange && !tokenExchangeInProgress.current) {
      tokenExchangeInProgress.current = loadingTokenExchange;
    }
  }, [loadingTokenExchange]);

  return (
    <AuthContext.Provider value={getAuthContextValue()}>
      {ariaLiveMessage && <AriaLive>{ariaLiveMessage}</AriaLive>}
      {shouldRenderChildren() && children}
    </AuthContext.Provider>
  );

  function shouldRenderChildren() {
    return (!shouldExchangeToken() && !tokenExchangeInProgress.current) || tokenExchangeError;
  }

  function getAuthContextValue() {
    return {
      appendLogoutHandlers,
      appendNativeLoginCancelHandlers,
      appendNativeLoginHandlers,
      appendPreLogoutHandlers,
      auth,
      authPending: tokenExchangeInProgress.current,
      cleanUpSession,
      environmentTier,
      handleAnonymousLogin: loginAvailable ? handleAnonymousLogin : warn,
      handleIdTokenOnlyExchange,
      handleLogin: loginAvailable ? handleLogin : warn,
      handleLogout: loginAvailable ? handleLogout : warn,
      handleTokenExchange,
      isLimitedAccess,
      isLoggedIn,
      isLoggedInWithCorporateRole,
      loadingTokenExchange,
      missingCorporateInformation,
      missingInformation,
      setPrepareTokenDowngradeOnUnload,
      storedLoginInfo,
      switchAuthType,
    };
  }

  function handleAddOauthWebViewMessage() {
    const idToken = getCookie("id_token");

    if (idToken) {
      setAuth({ idToken });
      Object.values(nativeLoginHandlersRef.current).forEach((handleNativeLoginCallback) => {
        handleNativeLoginCallback();
      });
      nativeLoginHandlersRef.current = {};
    }
  }

  function handleAuthEventWebViewMessage(value) {
    const { type } = decodeMessage(value);

    if (type === "USER_CANCEL") {
      Object.values(nativeLoginCancelRef.current).forEach((handleNativeLoginCancelCallback) => {
        handleNativeLoginCancelCallback();
      });
      nativeLoginCancelRef.current = {};
    }
  }

  function handleRemoveOauthWebViewMessage() {
    cleanUpSession();
  }

  function warn() {
    logger.warn("should not use AuthContext when loginAvailable is false");
  }

  function appendLogoutHandlers(logoutHandler, logoutHandlerKey) {
    const key = logoutHandlerKey || `${logoutHandler.name}-${getRandomString()}`;

    logoutHandlersRef.current = { ...logoutHandlersRef.current, [key]: logoutHandler };
  }

  function appendNativeLoginHandlers(nativeLoginHandler, nativeLoginHandlerKey) {
    const key = nativeLoginHandlerKey || `${nativeLoginHandler.name}-${getRandomString()}`;

    nativeLoginHandlersRef.current = {
      ...nativeLoginHandlersRef.current,
      [key]: nativeLoginHandler,
    };
  }

  function appendNativeLoginCancelHandlers(nativeLoginCancelHandler, nativeLoginCancelHandlerKey) {
    const key =
      nativeLoginCancelHandlerKey || `${nativeLoginCancelHandler.name}-${getRandomString()}`;

    nativeLoginCancelRef.current = {
      ...nativeLoginCancelRef.current,
      [key]: nativeLoginCancelHandler,
    };
  }

  function appendPreLogoutHandlers(preLogoutHandler, preLogoutHandlerKey) {
    const key = preLogoutHandlerKey || `${preLogoutHandler.name}-${getRandomString()}`;

    preLogoutHandlersRef.current = { ...preLogoutHandlersRef.current, [key]: preLogoutHandler };
  }

  function getRandomString() {
    return Math.random().toString(36).substring(2, 7);
  }

  function handleTokenExchangeAndMarkInProgress(token) {
    tokenExchangeInProgress.current = true;
    handleTokenExchange(token);
  }

  async function handleTokenExchange(token) {
    const companyId = auth["corporate.companyUserInformation.companyId"];
    const exchangeCompanyId = isCorporatePersona ? getCompanyIdFromToken() ?? companyId : null;

    await doTokenExchange(token, exchangeCompanyId);
  }

  async function switchAuthType(exchangeCompanyId = null) {
    const futurePersona = exchangeCompanyId ? personas.corporate : personas.leisure;

    await doTokenExchange(auth.idToken, exchangeCompanyId, futurePersona);
  }

  async function doTokenExchange(token, exchangeCompanyId, persona) {
    if (persona !== personas.corporate) {
      removeCorporateCookies();
    }
    const urlEncodedParams = new URLSearchParams();

    urlEncodedParams.append("assertion", token);
    urlEncodedParams.append("grant_type", loginSettings?.grantType);
    urlEncodedParams.append("scope", loginSettings?.scope);
    urlEncodedParams.append("client_id", getClientId(persona));
    exchangeCompanyId && urlEncodedParams.append("company_id", exchangeCompanyId);

    const body = await postTokenExchange("/api/security/v4/security/token", urlEncodedParams);

    if (isResponseOk(responseTokenExchange)) {
      removeStores();
      setAuth({ ...body, idToken: getCookie("id_token") });
    } else {
      cleanUpSession();
    }
    tokenExchangeInProgress.current = false;
  }

  async function handleIdTokenOnlyExchange(token) {
    const urlEncodedParams = new URLSearchParams();

    urlEncodedParams.append("grant_type", "urn:ietf:params:oauth:grant-type:token-exchange");
    urlEncodedParams.append("subject_token", token);
    urlEncodedParams.append("subject_token_type", "urn:ietf:params:oauth:token-type:id_token");
    urlEncodedParams.append("requested_token_type", "urn:ietf:params:oauth:token-type:id_token");

    const body = await postTokenExchange("/api/security/v4/security/token", urlEncodedParams);

    if (isResponseOk(responseTokenExchange)) {
      setAuth({ ...body, idToken: getCookie("id_token") });
    }

    tokenExchangeInProgress.current = false;
  }

  function getClientId(persona) {
    return persona
      ? loginSettings?.[persona]?.clientIds?.[environmentTier] ??
          loginSettings?.clientIds?.[environmentTier]
      : loginSettings?.clientIds?.[environmentTier];
  }

  function getAnonymousClientId() {
    return loginSettings?.anonymousTravelerLoginClientIds?.[environmentTier];
  }

  function getCompanyIdFromToken() {
    const corporateTokenCookie = getCookie("corporateToken");
    const decodedToken = decodeCookie(corporateTokenCookie);

    return decodedToken?.companyId;
  }

  function getCorporateRole() {
    return getCorporateUserInformation()?.role;
  }

  function getCorporateUserInformation() {
    const idToken = getCookie("id_token");

    return decodeCookie(idToken)?.apiContext?.corporateUserInformation ?? {};
  }

  async function handleLogin(formData, defaultUsername, defaultCompanyId) {
    const { companyId = defaultCompanyId, username = defaultUsername, rememberMe } = formData;
    const urlEncodedFormData = getUrlEncodedFormData(formData, username, companyId);
    const body = await postLogin("/api/security/v4/security/token", urlEncodedFormData);

    if (isResponseOk(responseLogin)) {
      const hasLimitedAccessToken =
        body?.["customers.userInformation.missingInformation"]?.length > 0 ||
        body?.["corporate.corporateUserInformation.missingInformation"]?.length > 0;

      setAuth({ ...body, idToken: getCookie("id_token") });
      setAriaLiveMessage(i18n("AuthProvider__LOGIN_SUCCESSFUL_ARIA"));
      fireTrack("squid", { page_description: "button:login" });
      setStoredLoginInfo({
        rememberMe,
        username: rememberMe ? username : "",
      });
      hasLimitedAccessToken && handleLoginLimitedAccessToken(body);
    }

    return { body, responseLogin };
  }

  async function handleAnonymousLogin(anonymousCompanyId) {
    const urlEncodedFormData = getAnonymousUrlEncodedFormData(anonymousCompanyId);

    const body = await postLogin("/api/security/v3/security/authorize", urlEncodedFormData);

    if (isResponseOk(responseLogin)) {
      setAuth({ ...body, idToken: getCookie("id_token") });
      setAriaLiveMessage(i18n("AuthProvider__LOGIN_SUCCESSFUL_ARIA"));
    }

    return { body, responseLogin };
  }

  function getUrlEncodedFormData(formData, username, companyId) {
    const urlEncodedFormData = getBaseUrlEncodedFormData(formData, username, companyId);

    urlEncodedFormData.append("client_id", getClientId());

    return urlEncodedFormData;
  }

  function getAnonymousUrlEncodedFormData(companyId) {
    const urlEncodedFormData = getBaseUrlEncodedFormData(
      { password: "none" },
      "ANONYMOUS",
      companyId
    );

    urlEncodedFormData.set("client_id", getAnonymousClientId());

    return urlEncodedFormData;
  }

  function getBaseUrlEncodedFormData(formData, username, companyId) {
    const urlEncodedFormData = new URLSearchParams();

    urlEncodedFormData.append("username", username);
    urlEncodedFormData.append("password", formData?.password);
    urlEncodedFormData.append("scope", loginSettings?.scope);
    urlEncodedFormData.append("response_type", loginSettings?.responseType);
    isCorporatePersona && urlEncodedFormData.append("company_id", companyId);

    return urlEncodedFormData;
  }

  function handleLoginLimitedAccessToken(body) {
    setAuth({ ...body, limitedAccess: true });
  }

  async function handleLogout() {
    handlePreLogoutCalls();
    await postLogout("/api/security/v4/security/logout");
    cleanUpSession();
    setAriaLiveMessage(i18n("AuthProvider__LOGOUT_SUCCESSFUL_ARIA"));
  }

  function handlePreLogoutCalls() {
    Object.values(preLogoutHandlersRef.current).forEach((handlePreLogoutCallback) => {
      handlePreLogoutCallback();
    });
  }

  function cleanUpSession() {
    setAuth({});
    Object.values(logoutHandlersRef.current).forEach((handleLogoutCallback) => {
      handleLogoutCallback();
    });
    removeStores();
    removeCookies(isCorporatePersona);
  }

  function removeStores() {
    if (isCorporatePersona) {
      removeCorporateStores();
    } else if (isMobileSite) {
      removeMwebStores();
    } else {
      removeVisionStores();
    }
  }

  function shouldHandleTokenExchange() {
    return shouldExchangeToken() && !tokenExchangeInProgress.current;
  }

  function shouldExchangeToken() {
    const leapfrogToken = getCookie("token");

    return !isLoggedIn() && !!leapfrogToken && hasLocaleChanged() && !isHybridEnabled();
  }

  // Is a Siebel account logged in (Rapid Rewards member, account or Swabiz traveler)
  function isLoggedIn() {
    return isCorporatePersona
      ? isLoggedInWithCorporateRole(corporateRoles.TRAVELER)
      : isAuthorized();
  }

  function isLoggedInWithCorporateRole(role) {
    return isAuthorized() && (isCorporatePersona ? getCorporateRole() === role : false);
  }

  function isAuthorized() {
    return (
      loginAvailable &&
      !!getCookie("id_token") &&
      !!auth.idToken &&
      (!isSwaWebView || isHybridEnabled())
    );
  }

  function isLimitedAccess() {
    return loginAvailable && !!auth.limitedAccess;
  }

  function shouldSetAuth() {
    return !!getCookie("id_token") && !auth.idToken && !isLimitedAccess();
  }

  function hasLocaleChanged() {
    const previousLocale = getCookie("locale");

    return previousLocale && previousLocale !== getCurrentLocale();
  }

  function getCurrentLocale() {
    const currentLocale = window.MP?.UrlLang;

    return availableLocales.includes(currentLocale) ? currentLocale : availableLocales[0];
  }
};

AuthContext.displayName = "AuthContext";
AuthProvider.propTypes = {
  /** Content to be rendered on the page. */
  children: PropTypes.node.isRequired,
};
